import { useSnackbar } from 'notistack';
import { useApi } from '../../shared/context';
import {
	DataRoomTeaser,
	StructureTrancheMap,
	StructureTranches,
	Tier1BankTypeWithBoth,
	Tier1BankTypeWithBothMap,
} from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import {
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import React from 'react';
import { FormikUtils } from '../../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { QueryKeys } from '../../shared/hooks';
import { getApiResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';

interface AddMatchedTeasersDialogProps {
	open: boolean;
	dataRoomGuid: string | undefined;
	close: () => void;
}

export const AddMatchedTeasersDialog: React.FC<AddMatchedTeasersDialogProps> = ({ open, dataRoomGuid, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const [tranche, setTranche] = React.useState<StructureTranches | undefined>(undefined);

	const matchedLendersMutation = useMutation(
		async ({ values }: AddMatchedLendersFormMutationVariables) => {
			const dto = {
				tranche: values.tranche,
				tier1: values.tier1,
			};

			const { data, error } = await getApiResponseData<DataRoomTeaser[]>(
				api.post(CerebroApiEndpoints.Teasers.Matched(dataRoomGuid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data: DataRoomTeaser[] | null) => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar(`${data?.length} matched lender teasers were added.`, { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<Dialog open={open} onClose={close}>
			<DialogTitle>Get Matched Lenders</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={
						{
							tranche: null,
							tier1: null,
						} as AddMatchedLendersFormValues
					}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(matchedLendersMutation.mutate)}
				>
					{({ isSubmitting, errors, touched, handleSubmit, values }) => {
						return (
							<CerebroForm onSubmit={handleSubmit}>
								<Grid
									container
									spacing={3}
									sx={{ marginTop: 3 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item xs={12}>
										<CerebroLabel id="tranche">Select Tranche</CerebroLabel>
										<Field
											id="tranche"
											as={CerebroDropdown}
											name="tranche"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('tranche', errors, touched)}
										>
											{Object.entries(StructureTrancheMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{FormikUtils.IsInvalid('tranche', errors, touched) && (
											<CerebroText variant="error">{errors.tranche}</CerebroText>
										)}
									</Grid>
									<Grid item xs={12}>
										<CerebroLabel id="tier1">Select Tier 1</CerebroLabel>
										<Field
											id="tier1"
											as={CerebroDropdown}
											name="tier1"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('tier1', errors, touched)}
										>
											{Object.entries(Tier1BankTypeWithBothMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
										{FormikUtils.IsInvalid('tier1', errors, touched) && (
											<CerebroText variant="error">{errors.tranche}</CerebroText>
										)}
									</Grid>
									<Grid item xs={8}>
										<CerebroPrimaryButton
											loading={isSubmitting}
											type="submit"
											sx={{ marginRight: 1 }}
										>
											Save
										</CerebroPrimaryButton>
										<CerebroSecondaryButton onClick={close}>Cancel</CerebroSecondaryButton>
									</Grid>
								</Grid>
							</CerebroForm>
						);
					}}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

type AddMatchedLendersFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddMatchedLendersFormValues>;
interface AddMatchedLendersFormValues {
	tranche: StructureTranches | null;
	tier1: Tier1BankTypeWithBoth | null;
}
type AddMatchedLendersFormValueErrors = FormikErrors<AddMatchedLendersFormValues>;

function validateForm(values: AddMatchedLendersFormValues) {
	const errors: AddMatchedLendersFormValueErrors = {};

	if (!values.tranche) {
		errors.tranche = 'Tranche is required';
	}

	if (!values.tier1) {
		errors.tier1 = 'Tier 1 is required';
	} else {
		if (values.tier1 == 'Other') {
			errors.tier1 = 'Other is not a valid matching choice and would return 0 results.';
		}
	}

	return errors;
}
